import classNames from 'classnames';
import { useEffect, useMemo } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { RoutePath } from 'src/router';

import { useSelector, useDispatch } from 'src/store';
import {
  selectedCompanyIdSelector,
  allCompanyOptionsSelector,
  isLoadingCompanySelector,
  isLoadingIsCompanyEmployersPayrollRequiredSelector,
  isLoadingPayrollReferenceMigrationAvailableSelector,
} from 'src/store/selectors/companySelector';
import {
  selectCompany,
  fetchIsCompanyEmployersPayrollRequiredAction,
  fetchPayrollReferenceMigrationAvailableAction,
} from 'src/store/slices/companySlice';

import { dropdownFilterOption } from '@itm/shared-frontend/lib/utils';
import Select, { SingleValue } from '@itm/shared-frontend/lib/components/react-select';

import { SelectOption } from 'src/types';

function Filters() {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const companyId = useSelector(selectedCompanyIdSelector);
  const companyOptions = useSelector(allCompanyOptionsSelector);
  const isLoadingCompanies = useSelector(isLoadingCompanySelector);
  const isLoadingIsCompanyEmployersPayrollRequired = useSelector(isLoadingIsCompanyEmployersPayrollRequiredSelector);
  const isLoadingPayrollReferenceMigrationAvailable = useSelector(isLoadingPayrollReferenceMigrationAvailableSelector);

  const isFilterDisabledByRoute = useMemo(
    () =>
      Boolean(
        matchPath(RoutePath.configurationAdminSyncViewEditEmployerAudit, pathname) ||
        matchPath(RoutePath.configurationAdminSyncViewEditEmployer, pathname) ||
        matchPath(`${RoutePath.configurationTemplateDefinitionEditRoot}/*`, pathname) ||
        matchPath(`${RoutePath.configurationPayrollViewRoot}/*`, pathname) ||
        matchPath(`${RoutePath.configurationPayrollEditRoot}/*`, pathname) ||
        matchPath(`${RoutePath.configurationValidationViewRoot}/*`, pathname) ||
        matchPath(`${RoutePath.configurationValidationEditRoot}/*`, pathname) ||
        matchPath(`${RoutePath.dataManagementUploadHistoryDetailsRoot}/*`, pathname) ||
        matchPath(`${RoutePath.dataManagementMemberViewEditRoot}/*`, pathname) ||
        matchPath(RoutePath.reportingReportsCreate, pathname) ||
        matchPath(RoutePath.reportingReportsEdit, pathname) ||
        matchPath(RoutePath.reportingLatePaymentReportDetail, pathname) ||
        matchPath(RoutePath.dataManagementMemberRequest, pathname),
      ),
    [pathname],
  );

  const selectedCompanyOption = useMemo(
    () => companyOptions.find((option) => option.value === companyId) || null,
    [companyOptions, companyId],
  );

  const companyChangeHandler = (newOption: SingleValue<SelectOption>) => {
    if (!newOption) return;
    dispatch(selectCompany(newOption.value));
  };

  useEffect(() => {
    if (!companyId) return;
    dispatch(fetchIsCompanyEmployersPayrollRequiredAction({ companyId }));
    dispatch(fetchPayrollReferenceMigrationAvailableAction({ companyId }));
  }, [dispatch, companyId]);

  return (
    <div className="is-flex is-justify-content-center pt-5">
      <div className="control is-fullwidth">
        <Select
          className={classNames('react-select', {
            'is-loading':
              isLoadingCompanies ||
              isLoadingIsCompanyEmployersPayrollRequired ||
              isLoadingPayrollReferenceMigrationAvailable,
          })}
          classNamePrefix="react-select"
          value={selectedCompanyOption}
          options={companyOptions}
          aria-label="Company name"
          placeholder={companyOptions.length || isLoadingCompanies ? 'Company name' : 'No companies'}
          onChange={companyChangeHandler}
          isDisabled={
            isFilterDisabledByRoute ||
            isLoadingCompanies ||
            isLoadingIsCompanyEmployersPayrollRequired ||
            isLoadingPayrollReferenceMigrationAvailable ||
            !companyOptions.length
          }
          isClearable={false}
          isSearchable={true}
          closeMenuOnSelect={true}
          filterOption={dropdownFilterOption}
        />
      </div>
    </div>
  );
}

export default Filters;
